/**
 * /admin/tenants/new - tenant onboarding wizard (CHAP-90 / Phase 1C #56).
 *
 * Single-page guided form: slug (with live availability check), display name,
 * and optional chapter metadata + brand colors. Creates a status=trial tenant
 * and routes to its dashboard.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../../lib/api.js';

interface SlugCheck {
  available: boolean;
  reason?: string;
  message?: string;
}

export default function AdminTenantNew() {
  const navigate = useNavigate();
  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [university, setUniversity] = useState('');
  const [chapterOrg, setChapterOrg] = useState('');
  const [address, setAddress] = useState('');
  const [memberNumberLabel, setMemberNumberLabel] = useState('');
  const [brandAccent, setBrandAccent] = useState('#cc785c');
  const [primaryColor, setPrimaryColor] = useState('#03244d');

  const [slugCheck, setSlugCheck] = useState<SlugCheck | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounced slug availability check.
  useEffect(() => {
    if (!slug) {
      setSlugCheck(null);
      return;
    }
    const h = setTimeout(() => {
      apiGet<SlugCheck>(`/api/admin/tenants/check-slug?slug=${encodeURIComponent(slug)}`)
        .then(setSlugCheck)
        .catch(() => setSlugCheck(null));
    }, 350);
    return () => clearTimeout(h);
  }, [slug]);

  const canSubmit = displayName.trim().length > 0 && slugCheck?.available === true && !submitting;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body = {
        slug: slug.toLowerCase(),
        displayName: displayName.trim(),
        university: university.trim() || undefined,
        chapterOrg: chapterOrg.trim() || undefined,
        address: address.trim() || undefined,
        memberNumberLabel: memberNumberLabel.trim() || undefined,
        brandAccent: brandAccent || undefined,
        primaryColor: primaryColor || undefined,
      };
      const r = await apiPost<{ ok: boolean; slug: string }>('/api/admin/tenants', body);
      navigate(`/c/${r.slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tenant.');
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4 min-w-0 max-w-2xl">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h2 className="text-navy text-xl sm:text-2xl font-bold">New chapter (tenant)</h2>
        <p className="text-neutral text-sm mt-1">Per CHAP-90. Creates a trial-status tenant.</p>
      </header>

      <form onSubmit={submit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-4 min-w-0">
        <Field label="URL slug" hint="lowercase, 3-32 chars, used in app.chapterbuild.com/c/<slug>" required>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            placeholder="omega-zeta"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            autoCapitalize="none"
          />
          {slug && slugCheck && (
            <p className={`text-xs mt-1 ${slugCheck.available ? 'text-green-700' : 'text-red-600'}`}>
              {slugCheck.available ? `✓ "${slug}" is available` : slugCheck.message}
            </p>
          )}
        </Field>

        <Field label="Display name" required>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Omega Zeta - Auburn LXA" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
        </Field>

        <div className="grid sm:grid-cols-2 gap-4 min-w-0">
          <Field label="University"><input value={university} onChange={(e) => setUniversity(e.target.value)} placeholder="Auburn University" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></Field>
          <Field label="Chapter org"><input value={chapterOrg} onChange={(e) => setChapterOrg(e.target.value)} placeholder="Lambda Chi Alpha" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></Field>
        </div>

        <Field label="Address"><input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="266 W Magnolia Ave, Auburn, AL" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></Field>

        <div className="grid sm:grid-cols-3 gap-4 min-w-0">
          <Field label="Member # label"><input value={memberNumberLabel} onChange={(e) => setMemberNumberLabel(e.target.value)} placeholder="Omega Number" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></Field>
          <Field label="Brand accent"><input type="color" value={brandAccent} onChange={(e) => setBrandAccent(e.target.value)} className="w-full h-9 border border-gray-300 rounded" /></Field>
          <Field label="Primary color"><input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="w-full h-9 border border-gray-300 rounded" /></Field>
        </div>

        {error && <div className="border border-red-200 bg-red-50 text-red-800 text-sm rounded p-2">{error}</div>}

        <div className="flex items-center gap-3 pt-2">
          <button type="submit" disabled={!canSubmit} className="px-4 py-2 rounded bg-navy text-white text-sm font-medium disabled:opacity-50">
            {submitting ? 'Creating…' : 'Create chapter'}
          </button>
          <button type="button" onClick={() => navigate('/admin')} className="px-4 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, hint, required, children }: { label: string; hint?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="text-sm font-medium text-navy">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      {hint && <span className="block text-xs text-neutral mb-1">{hint}</span>}
      <div className={hint ? '' : 'mt-1'}>{children}</div>
    </label>
  );
}
